/**
 * Product shelf — the things a planner can put on the board.
 *
 * A row of chips above the grid: the plant's products first (those with a
 * labor standard on one of its lines), then the non-production activities
 * (cleaning, changeover, …). Drag a chip onto a line and day and an order
 * appears there in the first free shift. With no search the row shows what
 * the plant has been making lately (the products in the loaded plan, most
 * used first) so it stays short; typing finds any product. A chip's hover
 * title says which lines it can run on.
 */
import SearchIcon from "@mui/icons-material/Search";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useDrag } from "react-dnd";

import { LaborStandard } from "../../../../services/labor-standards-api";
import { Product } from "../../../../services/products-api";
import { planner } from "../../planner-ui";
import { ManufacturingOrder } from "../utils/calculations";
import { PLAN_DND, ProductDragItem } from "./dnd";
import { activityOrderType, linesForProduct } from "./plan-edits";

const Chip: React.FC<{ product: Product; lines: string[]; activity: boolean }> = ({ product, lines, activity }) => {
	const [{ isDragging }, dragRef] = useDrag<ProductDragItem, void, { isDragging: boolean }>(
		() => ({
			type: PLAN_DND.PRODUCT,
			item: { product },
			collect: (monitor) => ({ isDragging: monitor.isDragging() }),
		}),
		[product]
	);
	const title = lines.length
		? `${product.name} · runs on ${lines.join(", ")} · drag onto a line and day`
		: `${product.name} · no labor standard yet · drag onto a line and day`;
	return (
		<Box
			ref={dragRef}
			title={title}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0.75,
				px: 1.25,
				py: 0.5,
				border: `${planner.borderWidth}px solid ${activity ? planner.hairlineStrong : planner.accentBorder}`,
				borderRadius: `${planner.radiusSm}px`,
				bgcolor: activity ? planner.surfaceSubtle : planner.surface,
				color: planner.ink,
				cursor: "grab",
				userSelect: "none",
				whiteSpace: "nowrap",
				opacity: isDragging ? 0.4 : 1,
				...planner.type.metaStrong,
				"&:hover": { borderColor: planner.accent, boxShadow: planner.shadowHover },
				"&:active": { cursor: "grabbing" },
			}}
		>
			<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: activity ? planner.weld : lines.length ? planner.accent : planner.unplanned, flexShrink: 0 }} />
			{product.name}
			{lines.length > 0 && !activity && (
				<Typography component="span" sx={{ ...planner.type.meta, fontFamily: planner.fontMono, color: planner.inkMuted }}>
					{lines.length === 1 ? lines[0] : `${lines.length} lines`}
				</Typography>
			)}
		</Box>
	);
};

export interface ProductShelfProps {
	products: Product[];
	standards: LaborStandard[];
	/** The plant's lines, so products are offered only when they can run here. */
	workcenters: string[];
	/** The loaded plan: what has been made lately leads the row. */
	orders: ManufacturingOrder[];
}

/** How many recently made products the row shows before you have to type. */
const RECENT_LIMIT = 14;

const ProductShelf: React.FC<ProductShelfProps> = ({ products, standards, workcenters, orders }) => {
	const [query, setQuery] = useState("");
	const handleQuery = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value), []);

	const { rows, hiddenCount } = useMemo(() => {
		const here = new Set(workcenters.map((w) => w.trim().toLowerCase()));
		const q = query.trim().toLowerCase();
		const useCount = new Map<number, number>();
		orders.forEach((o) => {
			if (o.productId) useCount.set(o.productId, (useCount.get(o.productId) ?? 0) + 1);
		});
		const all = products
			.filter((p) => p.isActive)
			.map((p) => ({ product: p, lines: linesForProduct(p, standards), activity: activityOrderType(p) !== null, uses: useCount.get(p.id) ?? 0 }))
			.filter((row) => row.activity || row.lines.some((l) => here.has(l.trim().toLowerCase())));
		if (q) {
			const found = all
				.filter((row) => row.product.name.toLowerCase().includes(q) || (row.product.itemNumber ?? "").toLowerCase().includes(q))
				.sort((a, b) => Number(a.activity) - Number(b.activity) || a.product.name.localeCompare(b.product.name));
			return { rows: found, hiddenCount: 0 };
		}
		const recent = all
			.filter((row) => !row.activity && row.uses > 0)
			.sort((a, b) => b.uses - a.uses || a.product.name.localeCompare(b.product.name))
			.slice(0, RECENT_LIMIT);
		const activities = all.filter((row) => row.activity).sort((a, b) => a.product.name.localeCompare(b.product.name));
		return { rows: [...recent, ...activities], hiddenCount: all.filter((row) => !row.activity).length - recent.length };
	}, [products, standards, workcenters, orders, query]);

	const productRows = rows.filter((r) => !r.activity);
	const activityRows = rows.filter((r) => r.activity);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				px: 1.75,
				py: 1,
				mb: 1.5,
				bgcolor: planner.surface,
				border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
				borderRadius: `${planner.radius}px`,
			}}
		>
			<Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
				<Typography sx={{ ...planner.labelCaps, color: planner.inkMuted }}>Make</Typography>
				<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, whiteSpace: "nowrap" }}>
					{query ? "Drag onto a line and day" : hiddenCount > 0 ? `Recent · type to find ${hiddenCount} more` : "Drag onto a line and day"}
				</Typography>
			</Box>
			<TextField
				size="small"
				value={query}
				onChange={handleQuery}
				placeholder="Find a product"
				InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: planner.inkMuted }} /></InputAdornment> }}
				sx={{ width: 180, flexShrink: 0, "& .MuiInputBase-root": { ...planner.type.body, bgcolor: planner.surface } }}
			/>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, overflowX: "auto", minWidth: 0, flex: 1, pb: 0.25 }}>
				{productRows.map((row) => (
					<Chip key={row.product.id} product={row.product} lines={row.lines} activity={false} />
				))}
				{productRows.length > 0 && activityRows.length > 0 && (
					<Box sx={{ width: 1, alignSelf: "stretch", bgcolor: planner.hairlineStrong, mx: 0.5, flexShrink: 0 }} />
				)}
				{activityRows.map((row) => (
					<Chip key={row.product.id} product={row.product} lines={row.lines} activity />
				))}
				{rows.length === 0 && (
					<Typography sx={{ ...planner.type.meta, color: planner.inkMuted }}>
						{query ? "Nothing matches." : "Nothing made lately · type to find a product."}
					</Typography>
				)}
			</Box>
		</Box>
	);
};

export default ProductShelf;
